/**
 * Volunteers — server state.
 *
 * The query-key registry is declared first and separately, because approving a
 * registration (D2) creates a volunteer and has to invalidate any volunteer list
 * already on screen. Keys live here so that invalidation is a reference rather
 * than a `['volunteers']` string typed from memory in another file — the kind of
 * near-miss that produces a list which silently never refreshes.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../components/common/Toast';
import {
  activateVolunteer,
  deactivateVolunteer,
  rejectVolunteer,
  setVolunteerStatus,
  deleteVolunteer,
  fetchVolunteer,
  fetchVolunteerAssignments,
  fetchVolunteerAttendance,
  createVolunteer,
  fetchVolunteerHistory,
  fetchVolunteerRegistrations,
  fetchVolunteers,
  updateVolunteer,
} from '../services/api/volunteers';
import type {
  VolunteerCreateBody,
  VolunteerListQuery,
  VolunteerUpdateBody,
} from '../services/api/volunteers';
import type { VolunteerStatus } from '@parinaam-vms/shared';

import { metricsKeys, registrationKeys, volunteerKeys } from './queryKeys';

export { volunteerKeys };

/**
 * `enabled` exists for the assignment picker (D5), which must not fetch until
 * someone has typed enough to search for. Passing a deliberately unmatchable
 * search term instead would still make the request, and would cache a result
 * under a key nothing else can reuse.
 */
export function useVolunteers(params: VolunteerListQuery, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: volunteerKeys.list(params),
    queryFn: () => fetchVolunteers(params),
    enabled: options?.enabled ?? true,
  });
}

export function useVolunteer(id: string | undefined) {
  return useQuery({
    queryKey: volunteerKeys.detail(id ?? ''),
    queryFn: () => fetchVolunteer(id as string),
    enabled: Boolean(id),
  });
}

/** The submissions behind this volunteer, and any duplicate flag (Plan D12). */
export function useVolunteerRegistrations(id: string | undefined) {
  return useQuery({
    queryKey: volunteerKeys.registrations(id ?? ''),
    queryFn: () => fetchVolunteerRegistrations(id as string),
    enabled: Boolean(id),
  });
}

export function useVolunteerAssignments(id: string | undefined) {
  return useQuery({
    queryKey: volunteerKeys.assignments(id ?? ''),
    queryFn: () => fetchVolunteerAssignments(id as string),
    enabled: Boolean(id),
  });
}

export function useVolunteerAttendance(id: string | undefined) {
  return useQuery({
    queryKey: volunteerKeys.attendance(id ?? ''),
    queryFn: () => fetchVolunteerAttendance(id as string),
    enabled: Boolean(id),
  });
}

/**
 * The audit trail for one volunteer (M2).
 *
 * `staleTime: 0` because the panel sits on the same screen as the edit form: a
 * cached history would show the record as it was before the change the user just
 * made, which is the one thing a history must never do.
 */
export function useVolunteerHistory(id: string | undefined) {
  return useQuery({
    queryKey: volunteerKeys.history(id ?? ''),
    queryFn: () => fetchVolunteerHistory(id as string),
    enabled: Boolean(id),
    staleTime: 0,
  });
}

/**
 * Destroy a volunteer record (Plan D12).
 *
 * Invalidates the submissions panel as well as the volunteer lists: deleting a
 * duplicate takes its registration with it, and the panel would otherwise keep
 * offering a submission whose volunteer no longer exists.
 */
export function useDeleteVolunteer() {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => deleteVolunteer(id, reason),
    onSuccess: (_result, { id }) => {
      queryClient.removeQueries({ queryKey: volunteerKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: volunteerKeys.all });
      void queryClient.invalidateQueries({ queryKey: registrationKeys.all });
      toast.success('Record removed.');
    },
    onError: (error) => toast.error(error, 'Could not remove the record.'),
  });
}

export function useUpdateVolunteer(id: string) {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: VolunteerUpdateBody) => updateVolunteer(id, body),
    onSuccess: (volunteer) => {
      // The response is the updated row, so the detail cache is set directly
      // rather than invalidated — the profile re-renders with no second fetch
      // and no flash of the old values.
      queryClient.setQueryData(volunteerKeys.detail(id), volunteer);
      // A name, city or status change alters what the list shows, and the list
      // is filtered and paginated server-side, so it has to be refetched.
      void queryClient.invalidateQueries({ queryKey: volunteerKeys.all });
      toast.success('Details saved.');
    },
    onError: (error) => toast.error(error, 'Could not save the details.'),
  });
}

/**
 * Add a volunteer by hand (M3).
 *
 * The API has had this since M1 and the portal never called it, which meant a
 * corporate volunteer — a record only staff can create, by definition — could be
 * made with `curl` and by no other means.
 */
export function useCreateVolunteer() {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: VolunteerCreateBody) => createVolunteer(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: volunteerKeys.all });
      toast.success('Volunteer added.');
    },
    onError: (error) => toast.error(error, 'Could not add the volunteer.'),
  });
}

/**
 * Move a volunteer's status.
 *
 * One hook for all four operations rather than four near-identical ones: they
 * differ only in which endpoint they call, and every caller wants the same cache
 * behaviour afterwards. The action name is passed in, so a screen reads
 * `mutate({ action: 'activate' })` and the intent is visible at the call site.
 *
 * `set` is the one that also needs a destination, since it is the only operation
 * with more than one — hence the optional `status`, which the other three
 * ignore. It is the admin override, and the only action here that does not
 * correspond to a decision with rules behind it.
 */
export type VolunteerStatusAction = 'activate' | 'deactivate' | 'reject' | 'set';

export function useChangeVolunteerStatus(id: string) {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      action,
      reason,
      status,
    }: {
      action: VolunteerStatusAction;
      reason?: string;
      status?: VolunteerStatus;
    }) => {
      if (action === 'activate') return activateVolunteer(id, reason);
      if (action === 'deactivate') return deactivateVolunteer(id, reason);
      if (action === 'set') {
        return setVolunteerStatus(id, status as VolunteerStatus, reason as string);
      }
      return rejectVolunteer(id, reason as string);
    },
    onSuccess: (volunteer) => {
      queryClient.setQueryData(volunteerKeys.detail(id), volunteer);
      // The directory, the submissions panel and the dashboard counts all move
      // when a status does — none of them can be left showing the old one.
      void queryClient.invalidateQueries({ queryKey: volunteerKeys.all });
      void queryClient.invalidateQueries({ queryKey: registrationKeys.all });
      void queryClient.invalidateQueries({ queryKey: metricsKeys.all });
      toast.success(
        `${[volunteer.first_name, volunteer.last_name].filter(Boolean).join(' ')} is now ${volunteer.status.toLowerCase()}.`
      );
    },
    onError: (error) => toast.error(error, 'Could not change the status.'),
  });
}
